import {
  TextButton,
  IconDelete,
  IconButton,
  IconAddCircle,
} from "@cdkglobal/radial";
import { JsonSchemaItems, RenderCoRelationRowsProps } from "../../../../types";
import {
  StyledCardRowWrapper,
  NormalRowWrapper,
  IconDeleteWrapper,
  InputsWrapper,
  RowsContainer,
  RecordPageFieldLayout,
} from "../../../../utils/styled";
import { FormattedMessage } from "react-intl";
import { resolveCustomStyle } from "../../style-config";
import {
  FIELD_KEYS_TO_HIDE_ACTIONS_WHEN_DISABLED,
  CORRELATION_ACTION_DISABLED_FIELD_KEYS,
} from "../../constants";

export default function RenderCoRelationRows({
  rows,
  propertyKey,
  property,
  isDeleteButtonDisabled,
  isAddButtonDisabled,
  isReadOnly,
  renderInputType,
  addRow,
  deleteRow,
}: Readonly<RenderCoRelationRowsProps>): JSX.Element {
  const { items: propertyItems } = property;
  const { properties, rowScopedProperties } =
    (propertyItems as JsonSchemaItems) || {};

  const shouldHideDeleteButton =
    (isDeleteButtonDisabled &&
      FIELD_KEYS_TO_HIDE_ACTIONS_WHEN_DISABLED.includes(propertyKey)) ||
    CORRELATION_ACTION_DISABLED_FIELD_KEYS.includes(propertyKey);
  const shouldHideAddButton =
    (isAddButtonDisabled &&
      FIELD_KEYS_TO_HIDE_ACTIONS_WHEN_DISABLED.includes(propertyKey)) ||
    CORRELATION_ACTION_DISABLED_FIELD_KEYS.includes(propertyKey);

  const propertyEntries = Object.entries(properties || {});

  return (
    <RowsContainer>
      {rows.map((id, rowIndex) => {
        const visibleFieldsCount = propertyEntries.filter(
          ([propertySubKey, item]) => {
            const scopedProperty =
              rowScopedProperties?.[rowIndex]?.[propertySubKey];
            const isHidden = scopedProperty?.isHidden ?? item?.isHidden;
            return !isHidden;
          },
        ).length;

        const WrapperComponent =
          visibleFieldsCount > 4 ? StyledCardRowWrapper : NormalRowWrapper;

        return (
          <WrapperComponent key={id}>
            <InputsWrapper>
              {propertyEntries.map(([propertySubKey, item]) => {
                const { isHidden } =
                  rowScopedProperties?.[rowIndex]?.[propertySubKey] ||
                  properties?.[propertySubKey] ||
                  {};
                return (
                  <RecordPageFieldLayout
                    key={propertySubKey}
                    $customStyle={resolveCustomStyle({
                      applicability: "field",
                      isHidden,
                      propertyKey: propertySubKey,
                      property: item,
                    })}
                  >
                    {renderInputType({
                      rowIndex,
                      propertySubKey,
                      itemProperty: item,
                    })}
                  </RecordPageFieldLayout>
                );
              })}
              {/* TBD: Fix styling for IconDeleteWrapper */}
              {!isReadOnly && !shouldHideDeleteButton && (
                <IconDeleteWrapper>
                  <IconButton
                    icon={<IconDelete />}
                    dataTestId={`delete-${propertyKey}-${rowIndex}-row-button`}
                    text={
                      <FormattedMessage
                        id="button.delete"
                        defaultMessage="Delete"
                      />
                    }
                    onClick={() => deleteRow(rowIndex)}
                    style={{ cursor: "pointer" }}
                    isDisabled={isDeleteButtonDisabled}
                  />
                </IconDeleteWrapper>
              )}
            </InputsWrapper>
          </WrapperComponent>
        );
      })}
      {!isReadOnly && !shouldHideAddButton && (
        <TextButton
          icon={<IconAddCircle />}
          dataTestId={`add-${propertyKey}-row-button`}
          style={{ marginBottom: "1rem" }}
          onClick={addRow}
          text={
            rows.length === 0 ? (
              <FormattedMessage id="button.add" defaultMessage="Add" />
            ) : (
              <FormattedMessage
                id="button.addAnother"
                defaultMessage="Add Another"
              />
            )
          }
          isDisabled={isAddButtonDisabled}
        />
      )}
    </RowsContainer>
  );
}
